// import { useNavigate } from "react-router-dom";
import { CORE_API } from "../constants";
import { Routes } from "../routes";
import { logoutUser } from "./user.service";

type THttpHeaders = { [key: string]: string };

type RequestBody =
  | undefined
  | null
  | boolean
  | string
  | number
  | Date
  | { [key: string]: RequestBody }
  | RequestBody[];

export const Regex = {
  id: /^[a-z0-9]+$/,
  mobile: /^([0-9]{10})$/,
  latitude:
    /^(\+|-)?(?:90(?:(?:\.0{1,6})?)|(?:[0-9]|[1-8][0-9])(?:(?:\.[0-9]{1,6})?))$/,
  longitude:
    /^(\+|-)?(?:180(?:(?:\.0{1,6})?)|(?:[0-9]|[1-9][0-9]|1[0-7][0-9])(?:(?:\.[0-9]{1,6})?))$/,
};

export interface APIOptions {
  basePath?: string;
  sendTokenInUrl?: boolean;
  sendTokenInHeader?: boolean;
  body?: RequestBody;
  headers?: THttpHeaders;
  usePutMethod?: boolean;
  useDeleteMethod?: boolean;
  usePatchMethod?: boolean;
  isBlob?: boolean;
  method?: string;
}

export async function handleErrors(response: Response) {
  if (!response.ok) {
    const err = await response.json();
    throw Error(err.error.message);
  }
  return response.status === 200 ? response.json() : undefined;
}

/**
 * Drops `access_token` from the address bar (other params, path and hash are kept). The token is
 * already stored by then, and left in the URL it ends up in browser history, server logs and
 * Referer headers.
 */
export function removeTokenFromUrl() {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has("access_token")) return;
    url.searchParams.delete("access_token");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
  } catch {
    // cosmetic only: never break the app (or an embedding page) over it
  }
}

export function getAccessToken(): string {
  const queryParams = new URLSearchParams(window.location.search);
  const token = queryParams.get("access_token");
  if (token) {
    setAccessToken(token);
    removeTokenFromUrl();
  }
  return localStorage.getItem("access_token") as string;
}

export function setAccessToken(access_token: string) {
  localStorage.setItem("access_token", access_token);
}

export function clearAccessToken() {
  localStorage.removeItem("access_token");
}

function addAccessToken(url: string): string {
  const qsSeperator = url.includes("?") ? "&" : "?";
  return url.concat(qsSeperator + "access_token=" + getAccessToken());
}

async function makeApiRequest(
  url: string,
  options: APIOptions = {},
  method: string
) {
  const basePath = options.basePath || CORE_API;
  let finalUrl = `${basePath}${url}`;

  const tokenRequiredInUrl = !!options.sendTokenInUrl;
  if (tokenRequiredInUrl) {
    finalUrl = addAccessToken(finalUrl);
  }

  const lang = localStorage.getItem("user_language") ?? "en";
  const finalOptions: APIOptions = {
    method,
    headers: { ...options.headers, lang },
  };

  const tokenRequiredInHeader = !!options.sendTokenInHeader;
  if (tokenRequiredInHeader) {
    if (!finalOptions.headers) finalOptions.headers = {};
    finalOptions.headers["Authorization"] = `Bearer ${getAccessToken()}`;
  }

  if (!finalOptions.headers?.hasOwnProperty("content-type")) {
    if (!finalOptions.headers) finalOptions.headers = {};
    finalOptions.headers["content-type"] = "application/json; charset=utf-8";
  }

  if (options.body) {
    finalOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(finalUrl, finalOptions as RequestInit);

  try {
    const responseData = options.isBlob
      ? await response.blob()
      : await response.json();

    if (responseData.error) {
      const { statusCode } = responseData.error;

      switch (statusCode) {
      case 401:
        if (window.location.pathname !== Routes.unauthorized) {
          logoutUser().then(() => {
            window.location.replace(Routes.unauthorized);
          });
        }
        break;
      default:
        return responseData;
      }
    } else {
      return responseData;
    }
  } catch {
    // An empty or non-JSON body on a successful response (e.g. 204 from a delete) is a success.
    // The same on a failed response (e.g. an HTML 502 from a gateway) is not: report it instead
    // of letting callers treat it as success.
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    return { message: "Success" };
  }
}

export async function getData(url = "", options: APIOptions = {}) {
  return makeApiRequest(url, options, "get");
}

export async function postData(url = "", options: APIOptions = {}) {
  const method = options.usePutMethod
    ? "put"
    : options.usePatchMethod
      ? "PATCH"
      : "post";
  return makeApiRequest(url, options, method);
}

export async function deleteData(url = "", options: APIOptions = {}) {
  const method = options.useDeleteMethod ? "delete" : "post";
  return makeApiRequest(url, options, method);
}

export async function updateData(url = "", options: APIOptions = {}) {
  const method = options.usePatchMethod ? "patch" : "post";
  return makeApiRequest(url, options, method);
}
